import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import { TOUR_LIST } from "../../mocks/tours";
import SeoMeta from "../../components/base/SeoMeta";

const BASE_URL = (import.meta.env.VITE_SITE_URL as string) || "https://flightfare.pk";

const TOURS_JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "International Tour Packages from Pakistan – flightfare.pk",
    "url": `${BASE_URL}/tours`,
    "description": "Book all-inclusive international tour packages from Pakistan with flightfare.pk. Visa, air ticket, hotel and sightseeing all covered. Best tours to Malaysia, Thailand, Dubai, Turkey and more."
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "International Tour Packages from Pakistan",
    "serviceType": "Tour Package",
    "provider": {
      "@type": "TravelAgency",
      "name": "flightfare.pk",
      "url": BASE_URL
    },
    "areaServed": "PK",
    "description": "All-inclusive international tours from Pakistan including visa, flights, hotel and sightseeing. Destinations include Malaysia, Thailand, Dubai, Istanbul, Europe and more.",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "PKR",
      "availability": "https://schema.org/InStock"
    }
  }
];

const TAG_COLORS: Record<string, string> = {
  BESTSELLER: "bg-amber-500",
  POPULAR: "bg-green-600",
  ADVENTURE: "bg-orange-500",
  FEATURED: "bg-cyan-600",
  ROMANTIC: "bg-rose-500",
  FAMILY: "bg-teal-600",
  "HIDDEN GEM": "bg-violet-600",
};

export default function ToursPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <SeoMeta
        title="International Tour Packages from Pakistan – Flights, Hotels & Visa | flightfare.pk"
        description="Book all-inclusive international tour packages from Pakistan on flightfare.pk. Air ticket, visa, hotel & sightseeing all covered. Best tours to Malaysia, Thailand, Dubai, Istanbul, Europe and more."
        keywords="international tours Pakistan, tour packages from Pakistan, Malaysia tour Pakistan, Thailand tour Lahore, Dubai tour package, Turkey tour Pakistan, holiday packages Pakistan"
        canonical="/tours"
        jsonLd={TOURS_JSON_LD}
      />

      {/* Hero Banner */}
      <div className="pt-16">
        <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0891b2 0%, #06b6d4 40%, #22d3ee 70%, #a3e8ef 100%)" }}>
          <div className="absolute top-0 left-0 right-0 h-2 bg-amber-400" />
          <div className="absolute top-6 right-24 w-10 h-10 rounded-full border-2 border-white/30" />
          <div className="absolute top-16 right-64 w-5 h-5 rounded-full border border-white/20" />
          <div className="absolute bottom-8 left-16 w-8 h-8 rounded-full border border-white/20" />
          <div className="absolute bottom-16 left-48 w-4 h-4 rounded-full border border-white/30" />
          <div className="max-w-7xl mx-auto px-4 py-12 md:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <h1 className="text-3xl font-extrabold text-white leading-tight tracking-wide uppercase">
                Book Your Next Holiday With Us
              </h1>
              <p className="text-white/90 text-sm mt-3 leading-relaxed">
                All-inclusive international tours from Pakistan — visa, air ticket, hotel &amp; sightseeing all covered.
              </p>
            </div>
            <div className="hidden md:block flex-shrink-0">
              <img
                src="https://public.readdy.ai/ai/img_res/4333b895bec2b421c56f4a15e00ecc9e.jpg"
                alt="International Tours"
                className="w-72 h-56 object-contain object-top"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Trending Tours */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Trending Tours</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {TOUR_LIST.map((tour) => (
              <a
                key={tour.slug}
                href={`/tours/${tour.slug}`}
                className="block rounded-lg overflow-hidden border border-gray-200 cursor-pointer hover:border-cyan-300 transition-all group"
              >
                <div className="w-full h-44 relative">
                  <img
                    src={tour.bannerImage}
                    alt={tour.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <span className={`absolute top-2 left-2 ${TAG_COLORS[tour.tag] || "bg-gray-600"} text-white text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wide`}>
                    {tour.tag}
                  </span>
                </div>
                <div className="p-4">
                  <p className="text-sm font-bold text-gray-900 mb-1 line-clamp-2 leading-snug">{tour.title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-2">{tour.subtitle}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{tour.nights}N / {tour.days}D</span>
                    <span className="text-xs text-cyan-600 font-semibold flex items-center gap-0.5">
                      View <i className="ri-arrow-right-line text-xs"></i>
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Embark on unforgettable international tours from Pakistan with flightfare.pk, your premium travel partner. We specialise in curating exceptional travel experiences to some of the world&apos;s most sought-after destinations. Whether you&apos;re dreaming of exploring the vibrant streets of Paris, the pristine beaches of Bali, or the rich cultural heritage of Istanbul, our expertly crafted tours ensure that every detail of your journey is meticulously planned. From flights and accommodations to guided tours and exclusive experiences, we handle it all, allowing you to travel with ease and confidence.
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            At flightfare.pk, we understand that every traveller is unique, which is why we offer a diverse range of international tour packages tailored to your interests and preferences. Whether you&apos;re seeking a romantic getaway, an adventurous expedition, or a family vacation, our extensive portfolio has something for everyone.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
